import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button, TextField } from '@material-ui/core';
import { CheckCircleOutline } from '@material-ui/icons';
import { get } from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { CAMPAIGN_CUSTOM_LOGIC_TYPES } from 'common/constants';
import classNames from 'client/helpers/classNames';
import { selectLastDonation } from './lastDonationSlice';
import {
    selectCampaignSlug,
    selectSelectedCampaign,
} from '../campaign/campaignSlice';

const useStyles = makeStyles(({ palette, spacing, typography }) => ({
    error: {
        color: palette.error.main,
        fontSize: typography.pxToRem(14),
        marginTop: spacing(1),
        marginBottom: spacing(1),
    },
    innerContainer: {
        color: palette.text.primary,
        maxWidth: 600,
        margin: 'auto',
        padding: spacing(2),
    },
    fieldContainer: {
        marginBottom: spacing(1),
    },
    label: {
        marginBottom: spacing(1),
        fontSize: '1rem',

        '@media (max-width: 600px)': {
            fontSize: typography.pxToRem(14),
            marginBottom: spacing(0.5),
        },
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
    },
    secondaryButton: {
        borderRadius: 'calc(0.5rem - 2px)',
        fontSize: '1rem',
        padding: spacing(0.5),
        '@media (max-width: 600px)': {
            fontSize: typography.pxToRem(14),
        },
    },
    primaryButton: {
        backgroundColor: palette.secondary.main,
        color: '#fff',
        borderRadius: 'calc(0.5rem - 2px)',
        fontSize: '1rem',
        padding: spacing(0.5),
        '&:hover': {
            backgroundColor: '#ff9700',
        },
        '@media (max-width: 600px)': {
            fontSize: typography.pxToRem(14),
        },
    },
    outlinedButton: {
        color: 'rgb(180,83,9)',
        borderRadius: 'calc(0.5rem - 2px)',
        fontSize: '1rem',
        padding: spacing(0.5),
        borderColor: 'rgb(252,211,77)',
        '&:hover': {
            backgroundColor: 'rgb(255,251,235)',
            color: palette.text.primary,
        },
        '@media (max-width: 600px)': {
            fontSize: typography.pxToRem(14),
        },
    },
    form: {
        marginTop: spacing(3),
    },
    container: {
        boxShadow:
            'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px',
        border: '1px solid rgb(253,230,138)',
        borderRadius: '0.5rem',
        marginTop: spacing(5),
        overflow: 'hidden',
    },
    textField: {
        '&::placeholder': {
            color: palette.grey[600],
        },
    },
    mainHeading: {
        fontSize: '1.1rem',
        marginTop: spacing(1),
        marginBottom: spacing(1),
        '@media (max-width: 600px)': {
            fontSize: typography.pxToRem(16),
        },
    },
    subHeading: {
        fontSize: '0.95rem',
        color: palette.grey[600],
        '@media (max-width: 600px)': {
            fontSize: typography.pxToRem(14),
        },
    },
    topStrip: {
        backgroundColor: 'rgb(255,251,235)',
        height: '0.5rem',
    },

    checkIconContainer: {
        width: '40px',
        height: '40px',
        margin: 'auto',
        borderRadius: '50%',
        backgroundColor: 'rgb(240, 253, 244)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: '4px',
    },
    checkIcon: {
        width: '25px !important',
        height: '25px !important',
    },
    marginTop20: {
        marginTop: '20px',
    },
    viewJournalButtonContainer: {
        maxWidth: '290px',
        margin: '20px auto 20px',
    },
}));

const getDisplayName = lastDonation => {
    const displayName = lastDonation.displayName?.trim();
    if (displayName) {
        return displayName;
    }
    const fullName = [
        lastDonation.firstName?.trim(),
        lastDonation.lastName?.trim(),
    ]
        .filter(Boolean)
        .join(' ');
    if (fullName) {
        return fullName;
    }
    return '';
};

function JournalEntryForm() {
    const classes = useStyles();
    const lastDonation = useSelector(selectLastDonation);
    const [formData, setFormData] = useState({
        name: getDisplayName(lastDonation),
        message: '',
        donationTierId: '',
        donationId: '',
    });
    const [errors, setErrors] = useState({});
    const [submissionError, setSubmissionError] = useState('');
    const campaign = useSelector(selectSelectedCampaign);
    const campaignSlug = useSelector(selectCampaignSlug);
    const journalUrl = `/${campaignSlug}/journal`;
    const VIEW = {
        FORM: 'form',
        THANKYOU: 'thankyou',
        ADD: 'add',
        DUPLICATE: 'duplicate',
    };
    const [formView, setFormView] = useState(VIEW.FORM);

    useEffect(() => {
        if (!formData.name) {
            setFormData({ ...formData, name: getDisplayName(lastDonation) });
        }
    }, [lastDonation]);

    const qualifiedTier = useMemo(() => {
        const customLogicType = get(
            campaign,
            'customLogicSettings.customLogicType',
            null,
        );
        const customLogicEnabled = get(
            campaign,
            'customLogicSettings.isEnabled',
            false,
        );
        const donationTiers = get(
            campaign,
            'donationSettings.donationTiers',
            null,
        );
        const currencyRates = get(
            campaign,
            'currencySettings.currencyRates',
            [],
        );
        const conversionRate = currencyRates.find(
            rate => rate.currency === lastDonation.currency,
        )?.rate;

        if (
            lastDonation.amount &&
            donationTiers &&
            customLogicType === CAMPAIGN_CUSTOM_LOGIC_TYPES.JOURNAL &&
            customLogicEnabled &&
            conversionRate
        ) {
            const sortedTiers = [...donationTiers].sort(
                (a, b) => b.minValue - a.minValue,
            );

            let recurringPeriod = 1;
            if (
                lastDonation.paymentMode === 'monthly' ||
                lastDonation.paymentMode === 'unlimited-monthly'
            ) {
                recurringPeriod = lastDonation.recurringMonths ?? 1;
            }

            const tier =
                sortedTiers.find(
                    tier =>
                        lastDonation.amount *
                            recurringPeriod *
                            conversionRate >=
                        tier.minValue,
                ) || null;
            return tier;
        }
        return null;
    }, [campaign, lastDonation]);

    const handleInputChange = e => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: '',
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Display Name is required';
        } else if (formData.name.length > 150) {
            newErrors.name = 'Display Name cannot exceed 150 characters';
        }
        if (formData.message) {
            const lineBreaks = (formData.message.match(/\n/g) || []).length;
            if (lineBreaks > 16) {
                newErrors.message = 'Maximum of 16 line breaks allowed';
            } else if (formData.message.length > 1800) {
                newErrors.message = 'Maximum of 1800 characters allowed';
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setSubmissionError('');

        if (validateForm()) {
            try {
                const response = await axios.post('public/journal/entry', {
                    ...formData,
                    donationId: lastDonation.donationId,
                    donationTierId: qualifiedTier.entityId,
                });

                if (response.status === 201) {
                    setFormView(VIEW.THANKYOU);
                } else if (response.data?.error) {
                    const errorMessage = response.data?.message;
                    if (errorMessage === 'duplicate request') {
                        setFormView(VIEW.DUPLICATE);
                        return;
                    }
                    setSubmissionError(`Request failed! ${errorMessage}`);
                }
            } catch (error) {
                console.log(error);
                setSubmissionError('Request failed');
            }
        }
    };

    const handleCancel = () => {
        setFormView(VIEW.ADD);
        setErrors({});
        setSubmissionError('');
    };

    const handleShowForm = () => {
        setFormView(VIEW.FORM);
    };

    const renderForm = () => {
        return (
            <div className={classes.innerContainer}>
                <div className={classes.mainHeading}>
                    Your donation qualifies for an Honoree Message!
                </div>
                <div className={classes.subHeading}>
                    Honor someone special by adding a message to our journal.
                </div>

                <form onSubmit={handleSubmit} className={classes.form}>
                    <div className={classes.fieldContainer}>
                        <div className={classes.label}>
                            <label htmlFor="journal-name">Display Name</label>
                        </div>
                        <TextField
                            id="journal-name"
                            placeholder="Name to display in journal"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className={classes.textField}
                            error={Boolean(errors.name)}
                            helperText={
                                errors.name || `${formData.name.length}/150`
                            }
                            FormHelperTextProps={{
                                style: {
                                    textAlign: errors.name ? 'left' : 'right',
                                },
                            }}
                            variant="outlined"
                            size="small"
                            fullWidth
                            inputProps={{ maxLength: 150 }}
                        />
                    </div>

                    <div className={classes.fieldContainer}>
                        <div className={classes.label}>
                            <label htmlFor="journal-message">Message</label>
                        </div>
                        <TextField
                            id="journal-message"
                            placeholder="Write your message"
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            className={classes.textField}
                            multiline
                            rows={4}
                            error={Boolean(errors.message)}
                            helperText={
                                errors.message ||
                                `${formData.message.length}/1800`
                            }
                            FormHelperTextProps={{
                                style: {
                                    textAlign: errors.message
                                        ? 'left'
                                        : 'right',
                                },
                            }}
                            variant="outlined"
                            size="small"
                            fullWidth
                            inputProps={{ maxLength: 1800 }}
                        />
                    </div>

                    {submissionError && (
                        <div className={classes.error}>{submissionError}</div>
                    )}

                    <div className={classes.buttonContainer}>
                        <Button
                            type="submit"
                            className={classes.primaryButton}
                            fullWidth
                            variant="contained"
                        >
                            Submit
                        </Button>
                        <Button
                            type="button"
                            variant="text"
                            className={classes.secondaryButton}
                            onClick={handleCancel}
                            fullWidth
                        >
                            I prefer not to add a message
                        </Button>
                    </div>
                </form>
            </div>
        );
    };

    const renderThankYou = () => (
        <div className={classes.innerContainer}>
            <div className={classes.checkIconContainer}>
                <CheckCircleOutline
                    htmlColor={'rgb(34,197,94)'}
                    className={classes.checkIcon}
                />
            </div>
            <div
                className={classNames(classes.mainHeading, classes.marginTop20)}
            >
                Thank You!
            </div>
            <div className={classes.subHeading}>
                Your message has been added to our journal.
            </div>
            <div
                className={classNames(
                    classes.buttonContainer,
                    classes.viewJournalButtonContainer,
                )}
            >
                <a href={journalUrl}>
                    <Button
                        type="button"
                        className={classes.primaryButton}
                        fullWidth
                        variant="contained"
                    >
                        View Journal
                    </Button>
                </a>
            </div>
        </div>
    );

    const renderDuplicateMessage = () => (
        <div className={classes.innerContainer}>
            <div
                className={classNames(classes.mainHeading, classes.marginTop20)}
            >
                Duplicate Request!
            </div>
            <div className={classes.subHeading}>
                A message for this donation was already added.
            </div>
            <div
                className={classNames(
                    classes.buttonContainer,
                    classes.viewJournalButtonContainer,
                )}
            >
                <a href={journalUrl}>
                    <Button
                        type="button"
                        className={classes.primaryButton}
                        fullWidth
                        variant="contained"
                    >
                        View Journal
                    </Button>
                </a>
            </div>
        </div>
    );

    const renderAddMessage = () => (
        <div className={classes.innerContainer}>
            <div
                className={classNames(classes.subHeading, classes.marginTop20)}
            >
                You&apos;ve chosen not to add a message.
            </div>
            <div
                className={classNames(
                    classes.buttonContainer,
                    classes.viewJournalButtonContainer,
                )}
            >
                <Button
                    type="submit"
                    className={classes.outlinedButton}
                    fullWidth
                    variant="outlined"
                    onClick={handleShowForm}
                >
                    Add a message instead
                </Button>
            </div>
        </div>
    );

    const renderView = () => {
        switch (formView) {
            case VIEW.FORM:
                return renderForm();
            case VIEW.ADD:
                return renderAddMessage();
            case VIEW.THANKYOU:
                return renderThankYou();
            case VIEW.DUPLICATE:
                return renderDuplicateMessage();
            default:
                return renderForm();
        }
    };

    return (
        <>
            {qualifiedTier?.entityId && (
                <div className={classes.container}>
                    <div className={classes.topStrip}></div>
                    {renderView()}
                </div>
            )}
        </>
    );
}

export default JournalEntryForm;
